import * as React from "react";
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import { toast } from "react-toastify";
import {
  AttributeNode,
  FenceColorAttribute,
  FenceHeightAttribute,
  FenceMaterialAttribute,
  FenceSegmentAttribute,
  FenceStyleAttribute,
} from "models/fenceAttribute";
import { ensure } from "utils";
import { FenceSegment } from "models/fenceSegment";
import { EstimateApi } from "api/api";
import { SessionStorageWrapper } from "models/session-storage-wapper";

type CascadingSelection = {
  isMaterialSelected: boolean;
  isStyleSelected: boolean;
  isColorSelected: boolean;
  isHeightSelected: boolean;
};

enum AttibuteTypesEnum {
  Material,
  Style,
  Color,
  Height,
}

type SelectFenceStyleModalProps = {
  selectedFenceSegment: FenceSegment;
  onCancelClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  setSelectedAttributes: (attributes: FenceSegmentAttribute[]) => void;
};

export default function SelectFenceStyleModal(props: SelectFenceStyleModalProps) {
  const initialMaterial = { id: "", name: "Select Material" };
  const initialStyle = { id: "", name: "Select Style" };
  const initialColor = { id: "", name: "Select Color" };
  const initialHeight = { id: "", name: "Select Height" };

  const [fenceMaterial, setFenceMaterial] = React.useState<AttributeNode>(initialMaterial);
  const [fenceStyle, setFenceStyle] = React.useState<AttributeNode>(initialStyle);
  const [fenceColor, setFenceColor] = React.useState<AttributeNode>(initialColor);
  const [fenceHeight, setFenceHeight] = React.useState<AttributeNode>(initialHeight);
  const [isSelectionComplete, setSelectionComplete] = React.useState(false);
  const [selectorVisibility, setSelectorVisibility] = React.useState<CascadingSelection>({
    isMaterialSelected: true,
    isColorSelected: false,
    isHeightSelected: false,
    isStyleSelected: false,
  });
  const [allAttributes, setAllAttributes] = React.useState<AttributeNode[]>([]);

  React.useEffect(() => {
    const catalogId = SessionStorageWrapper.getItem<string>("companyId");
    EstimateApi.getFenceAttributes(catalogId!)
      .then((data) => {
        setAllAttributes(data.attributes);
        if (!props.selectedFenceSegment.isIncomplete()) {
          setSelectorVisibility({ isMaterialSelected: true, isColorSelected: true, isHeightSelected: true, isStyleSelected: true });
          const fs = props.selectedFenceSegment;
          const material = ensure(data.attributes.find((x) => x.id === fs.material.id));
          const style = ensure(material.children!.find((x) => x.id === fs.style.id));
          const color = ensure(style.children!.find((x) => x.id === fs.color.id));
          const height = ensure(color.children!.find((x) => x.id === fs.height.id));
          setFenceMaterial(material);
          setFenceStyle(style);
          setFenceColor(color);
          setFenceHeight(height);
        }
      })
      .catch(() => toast.error("Could not load fence catalog"));
  }, [setAllAttributes, setFenceMaterial, setFenceColor, setFenceStyle, setFenceHeight, props.selectedFenceSegment]);

  const handleDropDownSelection =
    (attrId: string, atrType: AttibuteTypesEnum) => (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
      switch (atrType) {
        case AttibuteTypesEnum.Material:
          setSelectorVisibility({ isMaterialSelected: true, isColorSelected: false, isHeightSelected: false, isStyleSelected: true });
          setFenceMaterial(ensure(allAttributes.find((x) => x.id === attrId)));
          setFenceStyle(initialStyle);
          setFenceColor(initialColor);
          setFenceHeight(initialHeight);
          setSelectionComplete(false);
          break;
        case AttibuteTypesEnum.Style:
          setSelectorVisibility({ isMaterialSelected: true, isColorSelected: true, isHeightSelected: false, isStyleSelected: true });
          setFenceStyle(ensure(fenceMaterial.children!.find((x) => x.id === attrId)));
          setFenceColor(initialColor);
          setFenceHeight(initialHeight);
          setSelectionComplete(false);
          break;
        case AttibuteTypesEnum.Color:
          setSelectorVisibility({ isMaterialSelected: true, isColorSelected: true, isHeightSelected: true, isStyleSelected: true });
          setFenceColor(ensure(fenceStyle.children!.find((x) => x.id === attrId)));
          setFenceHeight(initialHeight);
          setSelectionComplete(false);
          break;
        case AttibuteTypesEnum.Height:
          setSelectorVisibility({ isMaterialSelected: true, isColorSelected: true, isHeightSelected: true, isStyleSelected: true });
          setFenceHeight(ensure(fenceColor.children!.find((x) => x.id === attrId)));
          setSelectionComplete(true);
          break;
      }
    };

  const handleSelectClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    props.setSelectedAttributes([
      new FenceMaterialAttribute(fenceMaterial.id, fenceMaterial.name),
      new FenceStyleAttribute(fenceStyle.id, fenceStyle.name),
      new FenceColorAttribute(fenceColor.id, fenceColor.name),
      new FenceHeightAttribute(fenceHeight.id, fenceHeight.name),
    ]);
  };

  const closeBtn = (
    <button
      className="btn-close btn-close-white ms-auto"
      onClick={props.onCancelClick}
      aria-label="Close"
    />
  );

  return (
    <Modal isOpen={true} modalClassName="modal-fullscreen-sm-down">
      <ModalHeader className="bg-primary text-white" close={closeBtn}>
        Select Fence Style
      </ModalHeader>
      <ModalBody style={{ paddingBottom: "200px" }}>
        {selectorVisibility.isMaterialSelected && (
          <AttributeSelector label="Material" availableAttributes={allAttributes} selectedType={AttibuteTypesEnum.Material} selectedAttr={fenceMaterial} onDropDownSelection={handleDropDownSelection} />
        )}
        {selectorVisibility.isStyleSelected && (
          <AttributeSelector label="Style" availableAttributes={fenceMaterial.children || []} selectedType={AttibuteTypesEnum.Style} selectedAttr={fenceStyle} onDropDownSelection={handleDropDownSelection} />
        )}
        {selectorVisibility.isColorSelected && (
          <AttributeSelector label="Color" availableAttributes={fenceStyle.children || []} selectedType={AttibuteTypesEnum.Color} selectedAttr={fenceColor} onDropDownSelection={handleDropDownSelection} />
        )}
        {selectorVisibility.isHeightSelected && (
          <AttributeSelector label="Height" availableAttributes={fenceColor.children || []} selectedType={AttibuteTypesEnum.Height} selectedAttr={fenceHeight} onDropDownSelection={handleDropDownSelection} />
        )}
      </ModalBody>
      <ModalFooter className="bg-light">
        <Button color="primary" onClick={handleSelectClick} disabled={!isSelectionComplete}>
          Select Style
        </Button>
        <Button outline color="secondary" onClick={props.onCancelClick}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}

type AttributeSelectorProps = {
  label: string;
  availableAttributes: Array<AttributeNode>;
  selectedAttr: AttributeNode;
  selectedType: AttibuteTypesEnum;
  onDropDownSelection(attrId: string, selectedType: AttibuteTypesEnum): React.MouseEventHandler<HTMLElement>;
};

function AttributeSelector(props: AttributeSelectorProps) {
  const [dropdownOpen, setDropdownOpen] = React.useState(false);
  const isSelected = props.selectedAttr.id !== "";

  return (
    <div className="d-flex align-items-center gap-2 py-2 border-bottom">
      <span className="ds-label" style={{ width: "70px", flexShrink: 0 }}>
        {props.label}
      </span>
      <Dropdown isOpen={dropdownOpen} toggle={() => setDropdownOpen((s) => !s)} className="flex-fill">
        <DropdownToggle color={isSelected ? "primary" : "light"} caret className="w-100 text-start">
          {props.selectedAttr.name}
        </DropdownToggle>
        <DropdownMenu>
          {props.availableAttributes.map((attr) => (
            <DropdownItem key={attr.id} onClick={props.onDropDownSelection(attr.id, props.selectedType)}>
              {attr.name}
            </DropdownItem>
          ))}
        </DropdownMenu>
      </Dropdown>
      {props.selectedAttr.imageUrl && (
        <img
          src={props.selectedAttr.imageUrl}
          alt={props.selectedAttr.name}
          style={{ width: "48px", height: "48px", objectFit: "cover", borderRadius: "4px", flexShrink: 0 }}
        />
      )}
    </div>
  );
}
